/**
 * "Return to where I came from" for settings pages. A link to Settings carries the page it was
 * opened from in the router's location state; the settings shell turns it into a "Return to the
 * card / board" link. State (not a global variable) survives a reload and Back/Forward, and only
 * in-app paths are honoured. The card itself is loaded again through the normal, authorized
 * routes: nothing about it is stored here.
 */
export interface ReturnState {
  returnTo: string;
}

const BOARD_PATH = /^\/app\/boards\/[^/?#]+(\/cards\/[^/?#]+)?(\?.*)?$/;
const WORKSPACE_PATH = /^\/app\/workspaces\/[^/?#]+(\/calendar(\/cards\/[^/?#]+)?)?(\?.*)?$/;

export function isReturnPath(value: unknown): value is string {
  return typeof value === 'string' && (BOARD_PATH.test(value) || WORKSPACE_PATH.test(value));
}

interface LocationLike {
  pathname: string;
  search: string;
  state: unknown;
}

/** The state to put on a link to Settings from `location` (or `undefined`: nothing to return to). */
export function returnStateFrom(location: LocationLike): ReturnState | undefined {
  const inherited = readReturnTarget(location);
  if (inherited) return { returnTo: inherited }; // moving between settings pages keeps the origin
  const here = `${location.pathname}${location.search}`;
  return isReturnPath(here) ? { returnTo: here } : undefined;
}

export function readReturnTarget(location: { state: unknown }): string | null {
  const state = location.state;
  if (typeof state === 'object' && state !== null && 'returnTo' in state) {
    const { returnTo } = state;
    return isReturnPath(returnTo) ? returnTo : null;
  }
  return null;
}

export type ReturnKind = 'card' | 'board' | 'workspace';

export function returnKind(path: string): ReturnKind {
  if (path.includes('/cards/')) return 'card';
  return path.startsWith('/app/boards/') ? 'board' : 'workspace';
}
